import React, { useState, useEffect } from 'react';
import { apiFetch } from '../lib/auth';
import { BarChart3, TrendingUp, Users, RotateCcw, AlertTriangle, Calendar, Download, PieChart as PieIcon, Coins, Scissors } from 'lucide-react';

const COHORT_DATA = [
  { cohort: 'May 2026', size: 142, m0: '100%', m1: '24%', m2: '18%', m3: '16%', m4: '14%', m5: '12%' },
  { cohort: 'Jun 2026', size: 189, m0: '100%', m1: '28%', m2: '21%', m3: '19%', m4: '15%', m5: '—' },
  { cohort: 'Jul 2026', size: 215, m0: '100%', m1: '31%', m2: '24%', m3: '20%', m4: '—', m5: '—' },
  { cohort: 'Aug 2026', size: 260, m0: '100%', m1: '34%', m2: '26%', m3: '—', m4: '—', m5: '—' },
  { cohort: 'Sep 2026', size: 310, m0: '100%', m1: '32%', m2: '—', m3: '—', m4: '—', m5: '—' },
  { cohort: 'Oct 2026', size: 178, m0: '100%', m1: '—', m2: '—', m3: '—', m4: '—', m5: '—' },
];

const RETURN_REASONS = [
  { reason: 'Size Ran Larger Than Expected', count: 48, percent: 40, color: '#C9A84C' },
  { reason: 'Size Ran Smaller / Tight Chest', count: 32, percent: 27, color: '#E0C77D' },
  { reason: 'Changed Mind / Color Variation', count: 24, percent: 20, color: '#8A8A8E' },
  { reason: 'Drape / Silhouette Preference', count: 16, percent: 13, color: '#55555A' },
];



export default function ReportsPage() {
  const [timeRange, setTimeRange] = useState('90d');
  const [valuation, setValuation] = useState<{
    totalUnitsOnHand: number;
    totalRetailValueMinor: number;
    totalAssetCostMinor: number;
    categoryBreakdown: Array<{ category: string; units: number; retailValueMinor: number; assetCostMinor: number }>;
  } | null>(null);

  const [sellThrough, setSellThrough] = useState<Array<{
    size: string;
    onHand: number;
    sold: number;
    totalProcured: number;
    sellThroughPercent: number;
  }>>([]);

  useEffect(() => {
    apiFetch<{ data: any }>('/analytics/inventory-valuation')
      .then(res => setValuation(res.data))
      .catch(() => {
        setValuation({
          totalUnitsOnHand: 0,
          totalRetailValueMinor: 0,
          totalAssetCostMinor: 0,
          categoryBreakdown: [],
        });
      });

    apiFetch<{ data: any[] }>('/analytics/sell-through')
      .then(res => setSellThrough(res.data || []))
      .catch(() => {
        setSellThrough([]);
      });
  }, []);

  const fmtCurrency = (minor: number) => `₹${(minor / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Intelligence & Cohort Reports</h1>
          <p style={styles.subtitle}>Retention curves, fit & return telemetry, and product category diagnostics</p>
        </div>
        <div style={styles.headerActions}>
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} style={styles.select}>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Year to Date (2026)</option>
          </select>
          <button style={styles.exportBtn}>
            <Download size={15} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <TrendingUp size={15} color="#10B981" /> 60-Day Repeat Rate
          </div>
          <div style={styles.metricVal}>28.4%</div>
          <div style={styles.metricSub}>+4.1% vs previous quarter</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <RotateCcw size={15} color="#C9A84C" /> Overall Return Rate
          </div>
          <div style={styles.metricVal}>5.4%</div>
          <div style={styles.metricSub}>Industry benchmark: 14.8%</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <Users size={15} color="#C9A84C" /> Average 90-Day LTV
          </div>
          <div style={styles.metricVal}>₹21,450</div>
          <div style={styles.metricSub}>Across all active buyers</div>
        </div>
      </div>

      {/* Cohort Retention Table */}
      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>Customer Retention Cohorts</h3>
            <p style={styles.sectionSubtitle}>Percentage of newly acquired buyers making repeat orders over successive months</p>
          </div>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Acquisition Cohort</th>
                <th style={styles.th}>Buyers</th>
                <th style={styles.th}>Month 0</th>
                <th style={styles.th}>Month 1</th>
                <th style={styles.th}>Month 2</th>
                <th style={styles.th}>Month 3</th>
                <th style={styles.th}>Month 4</th>
                <th style={styles.th}>Month 5</th>
              </tr>
            </thead>
            <tbody>
              {COHORT_DATA.map((row) => (
                <tr key={row.cohort} style={styles.tr}>
                  <td style={styles.tdBold}>{row.cohort}</td>
                  <td style={styles.td}>{row.size}</td>
                  <td style={styles.cohortCell(100)}>{row.m0}</td>
                  <td style={styles.cohortCell(parseInt(row.m1) || 0)}>{row.m1}</td>
                  <td style={styles.cohortCell(parseInt(row.m2) || 0)}>{row.m2}</td>
                  <td style={styles.cohortCell(parseInt(row.m3) || 0)}>{row.m3}</td>
                  <td style={styles.cohortCell(parseInt(row.m4) || 0)}>{row.m4}</td>
                  <td style={styles.cohortCell(parseInt(row.m5) || 0)}>{row.m5}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Two Columns: Return telemetry + Category Performance */}
      <div style={styles.columnsRow}>
        {/* Return Reasons */}
        <div style={styles.columnCard}>
          <h3 style={styles.sectionTitle}>Return & Sizing Reasons</h3>
          <p style={styles.sectionSubtitle}>Telemetry from customer exchange and returns portal</p>

          <div style={styles.returnsList}>
            {RETURN_REASONS.map(item => (
              <div key={item.reason} style={styles.returnItem}>
                <div style={styles.returnHeader}>
                  <span style={{ fontSize: 13, color: '#FFF' }}>{item.reason}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.percent}% ({item.count})</span>
                </div>
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${item.percent}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Contribution */}
        <div style={styles.columnCard}>
          <h3 style={styles.sectionTitle}>Category Revenue & Quality</h3>
          <p style={styles.sectionSubtitle}>Volume contribution and return rates across garment lines</p>

          <div style={styles.categoryList}>
            {valuation?.categoryBreakdown && valuation.categoryBreakdown.length > 0 ? (
              valuation.categoryBreakdown.map(cat => (
                <div key={cat.category} style={styles.catRow}>
                  <div>
                    <div style={{ color: '#FFF', fontWeight: 600, fontSize: 13 }}>{cat.category}</div>
                    <div style={{ fontSize: 11, color: '#71717A' }}>{cat.units} pieces on-hand</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#10B981', fontWeight: 600, fontSize: 13 }}>₹{(cat.retailValueMinor / 100).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 11, color: '#8A8A8E' }}>Cost: ₹{(cat.assetCostMinor / 100).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '24px 12px', textAlign: 'center', color: '#8A8A8E', fontSize: 12 }}>
                No category sales recorded yet. Data will populate dynamically as orders and catalog items are processed.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 3: Inventory Valuation & Capital Breakdown (Step 2.10) ── */}
      {valuation && (
        <div style={{ ...styles.sectionCard, marginTop: 24 }}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Inventory Asset Valuation & Capital Breakdown</h3>
              <p style={styles.sectionSubtitle}>Balance sheet on-hand asset values (cost basis vs retail realization potential)</p>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 11, color: '#8A8A8E' }}>Total Asset Cost Basis:</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#C9A84C' }}>{fmtCurrency(valuation.totalAssetCostMinor)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 11, color: '#8A8A8E' }}>Retail Realization:</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10B981' }}>{fmtCurrency(valuation.totalRetailValueMinor)}</div>
              </div>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Garment Category</th>
                  <th style={styles.th}>On-Hand Units</th>
                  <th style={styles.th}>Capital at Cost (₹)</th>
                  <th style={styles.th}>Retail Realization (₹)</th>
                  <th style={styles.th}>Gross Margin Potential</th>
                </tr>
              </thead>
              <tbody>
                {valuation.categoryBreakdown.map(cat => {
                  const marginPercent = cat.retailValueMinor > 0
                    ? (((cat.retailValueMinor - cat.assetCostMinor) / cat.retailValueMinor) * 100).toFixed(1)
                    : '60.0';
                  return (
                    <tr key={cat.category} style={styles.tr}>
                      <td style={styles.tdBold}>{cat.category}</td>
                      <td style={styles.td}>{cat.units} units</td>
                      <td style={{ ...styles.td, color: '#C9A84C', fontWeight: 600 }}>{fmtCurrency(cat.assetCostMinor)}</td>
                      <td style={{ ...styles.td, color: '#10B981', fontWeight: 600 }}>{fmtCurrency(cat.retailValueMinor)}</td>
                      <td style={{ ...styles.td, color: '#FFF' }}>{marginPercent}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 4: Size-Curve & Sell-Through Analysis (Step 2.10) ── */}
      {sellThrough.length > 0 && (
        <div style={{ ...styles.sectionCard, marginTop: 24 }}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Garment Size-Curve & Sell-Through Analysis</h3>
              <p style={styles.sectionSubtitle}>Velocity metrics comparing units sold against units stocked to guide production buying</p>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Garment Size</th>
                  <th style={styles.th}>Total Procured</th>
                  <th style={styles.th}>Units Sold</th>
                  <th style={styles.th}>Current Stock</th>
                  <th style={styles.th}>Sell-Through Rate (%)</th>
                </tr>
              </thead>
              <tbody>
                {sellThrough.map(st => (
                  <tr key={st.size} style={styles.tr}>
                    <td style={styles.tdBold}>{st.size}</td>
                    <td style={styles.td}>{st.totalProcured}</td>
                    <td style={{ ...styles.td, color: '#10B981', fontWeight: 600 }}>{st.sold}</td>
                    <td style={styles.td}>{st.onHand}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 100, height: 6, background: '#333', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${st.sellThroughPercent}%`, height: '100%', background: st.sellThroughPercent >= 70 ? '#10B981' : '#C9A84C' }} />
                        </div>
                        <span style={{ fontWeight: 600, color: '#FFF', fontSize: 12 }}>{st.sellThroughPercent}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  headerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  select: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#141414',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 14px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  metricCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: '18px 20px',
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#8A8A8E',
    marginBottom: 8,
  },
  metricVal: {
    fontSize: 24,
    fontWeight: 600,
    color: '#FFF',
    marginBottom: 4,
  },
  metricSub: {
    fontSize: 11,
    color: '#71717A',
  },
  sectionCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
  },
  sectionHeader: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 18,
    fontWeight: 600,
    color: '#FFF',
    margin: '0 0 4px 0',
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    margin: 0,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  th: {
    padding: '10px 14px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
  },
  tr: {
    borderBottom: '1px solid #1F1F1F',
  },
  td: {
    padding: '12px 14px',
    fontSize: 12,
    color: '#D4D4D8',
  },
  tdBold: {
    padding: '12px 14px',
    fontSize: 12,
    color: '#FFF',
    fontWeight: 600,
  },
  cohortCell: (pct: number) => ({
    padding: '12px 14px',
    fontSize: 12,
    fontWeight: 500,
    color: pct > 0 ? '#C9A84C' : '#666',
    background: pct > 0 ? `rgba(201, 168, 76, ${Math.min(0.35, pct / 180)})` : 'transparent',
  }),
  columnsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 20,
  },
  columnCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: 20,
  },
  returnsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 16,
  },
  returnItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  returnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  barTrack: {
    height: 6,
    background: '#222',
    borderRadius: 3,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
  },
  categoryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginTop: 16,
  },
  catRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: '#1A1A1A',
    borderRadius: 6,
    border: '1px solid #242424',
  },
};
